import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, User, Globe, Palette, Bell, Shield, HelpCircle, LogOut, ChevronRight, Sparkles, Gift, Smartphone, Tag, Sun, Moon, Monitor, Clock, RotateCcw, CreditCard, Heart, Check, X, DollarSign, MessageCircle, Zap, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { InstallButton } from '../components/InstallPWAPrompt';
import NotificationSettings from '../components/NotificationSettings';
import IncomeAdjustmentModal from '../components/IncomeAdjustmentModal';
import IncomeSourcesModal from '../components/IncomeSourcesModal';
import ContactSupportModal from '../components/ContactSupportModal';
import BottomNav from '../components/BottomNav';
import { useTutorial } from '../components/OnboardingTutorial';
import { toast } from 'sonner';
import api, { changeSubscription, cancelSubscription } from '../lib/api';

const OnboardingTutorial = lazy(() => import('../components/OnboardingTutorial'));

const AI_PERSONALITIES = [
  { id: 'pote', emoji: '😎', name_fr: 'Pote', name_en: 'Buddy', name_pt: 'Amigo' },
  { id: 'coach', emoji: '💪', name_fr: 'Coach', name_en: 'Coach', name_pt: 'Treinador' },
  { id: 'bienveillant', emoji: '🤗', name_fr: 'Bienveillant', name_en: 'Caring', name_pt: 'Carinhoso' },
  { id: 'data_analyst', emoji: '📊', name_fr: 'Data Analyst', name_en: 'Data Analyst', name_pt: 'Analista' },
  { id: 'sarcastic', emoji: '😏', name_fr: 'Sarcastic', name_en: 'Sarcastic', name_pt: 'Sarcástico' },
];

const SITUATIONS = [
  { id: 'single', emoji: '👤', name_fr: 'Célibataire', name_en: 'Single', name_pt: 'Solteiro' },
  { id: 'couple', emoji: '👫', name_fr: 'En couple', name_en: 'Couple', name_pt: 'Casal' },
  { id: 'family', emoji: '👨‍👩‍👧‍👦', name_fr: 'Famille', name_en: 'Family', name_pt: 'Família' },
];

const THEME_OPTIONS = [
  { id: 'light', icon: Sun, label_fr: 'Clair', label_en: 'Light', label_pt: 'Claro' },
  { id: 'dark', icon: Moon, label_fr: 'Sombre', label_en: 'Dark', label_pt: 'Escuro' },
  { id: 'system', icon: Monitor, label_fr: 'Système', label_en: 'System', label_pt: 'Sistema' },
  { id: 'auto', icon: Clock, label_fr: 'Auto (20h-7h)', label_en: 'Auto (8PM-7AM)', label_pt: 'Auto (20h-7h)' },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateProfile, checkAuth } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();
  const { resetTutorial, shouldShow: showTutorial } = useTutorial();
  
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showIncomeAdjustment, setShowIncomeAdjustment] = useState(false);
  const [showIncomeSources, setShowIncomeSources] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [tutorialActive, setTutorialActive] = useState(false);
  const [usageDetails, setUsageDetails] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  
  // Check if user is a child - hide certain sections
  const isChild = user?.family_role === 'child';
  const [updatingSubscription, setUpdatingSubscription] = useState(false);

  // Show tutorial after component is mounted
  useEffect(() => {
    if (showTutorial) {
      const timer = setTimeout(() => setTutorialActive(true), 500);
      return () => clearTimeout(timer);
    }
  }, [showTutorial]);

  // Auto-open the IncomeSourcesModal when navigated with ?openIncomeSources=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openIncomeSources') === '1') {
      setShowIncomeSources(true);
      // Clean up the query param so refresh doesn't re-open it
      navigate('/settings', { replace: true });
    }
  }, [location.search, navigate]);

  // Fetch usage details
  useEffect(() => {
    const fetchUsageDetails = async () => {
      try {
        const response = await api.get('/subscription/usage-details');
        setUsageDetails(response.data);
      } catch (error) {
        console.error('Error fetching usage details:', error);
      } finally {
        setLoadingUsage(false);
      }
    };
    fetchUsageDetails();
  }, []);

  // Fetch subscription plans for the modal
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get('/subscription/plans');
        setPlans(response.data.plans || []);
      } catch (error) {
        console.error('Error fetching plans:', error);
      }
    };
    fetchPlans();
  }, []);

  // Helper to format limit display
  const formatLimit = (value) => {
    if (value === -1) return language === 'fr' ? 'Illimité' : language === 'pt' ? 'Ilimitado' : 'Unlimited';
    if (value === 0) return language === 'fr' ? 'Non disponible' : language === 'pt' ? 'Não disponível' : 'Not available';
    return value;
  };

  // Helper to format plan features for display
  const getPlanDescription = (plan) => {
    if (!plan?.features) return '';
    const f = plan.features;
    const parts = [];
    
    if (f.family_members === -1) {
      parts.push(language === 'fr' ? 'Membres illimités' : language === 'pt' ? 'Membros ilimitados' : 'Unlimited members');
    } else if (f.family_members > 0) {
      parts.push(`${f.family_members} ${language === 'fr' ? 'membres' : language === 'pt' ? 'membros' : 'members'}`);
    }
    
    if (f.ai_questions === -1) {
      parts.push(language === 'fr' ? 'IA illimitée' : language === 'pt' ? 'IA ilimitada' : 'Unlimited AI');
    } else if (f.ai_questions > 0) {
      parts.push(`${f.ai_questions} ${language === 'fr' ? 'questions IA/mois' : language === 'pt' ? 'perguntas IA/mês' : 'AI questions/month'}`);
    }
    
    if (f.voice_ai) {
      parts.push(language === 'fr' ? 'assistant vocal' : language === 'pt' ? 'assistente de voz' : 'voice assistant');
    }
    
    if (f.full_export) {
      parts.push('export');
    }
    
    return parts.join(', ');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSubscriptionChange = async (newPlan) => {
    setUpdatingSubscription(true);
    try {
      const response = await changeSubscription(newPlan);
      await checkAuth(); // Refresh user data
      setShowSubscriptionModal(false);
      toast.success(response.data.message || `Plan ${newPlan} ${t('activated')}!`);
    } catch (error) {
      console.error('Failed to update subscription:', error);
      const errorMsg = error.response?.data?.detail || t('failed_change_plan');
      toast.error(errorMsg);
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    setUpdatingSubscription(true);
    try {
      const response = await cancelSubscription();
      await checkAuth();
      setShowCancelModal(false);
      toast.success(response.data.message || t('subscription_cancelled'));
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      const errorMsg = error.response?.data?.detail || t('error');
      toast.error(errorMsg);
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handlePersonalityChange = async (personality) => {
    try {
      await updateProfile({ ai_personality: personality });
    } catch (error) {
      console.error('Failed to update personality:', error);
    }
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try {
      await updateProfile({ language: lang });
    } catch (error) {
      console.error('Failed to update language:', error);
    }
  };
  
  const handleResetTutorial = () => {
    resetTutorial();
    navigate('/dashboard');
  };

  const handleSituationChange = async (situation) => {
    try {
      await updateProfile({ situation });
    } catch (error) {
      console.error('Failed to update situation:', error);
    }
  };

  const getCurrentSituation = () => {
    return SITUATIONS.find(s => s.id === user?.situation) || SITUATIONS[0];
  };

  const getCurrentPersonality = () => {
    return AI_PERSONALITIES.find(p => p.id === user?.ai_personality) || AI_PERSONALITIES[0];
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">{t('settings')}</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card data-testid="profile-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-primary" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{user?.name}</h2>
                  <p className="text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Subscription - Hidden for children */}
        {!isChild && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <h3 className="text-sm font-medium text-muted-foreground mb-3 px-1">
              {t('subscription')}
            </h3>
            <Card data-testid="subscription-card">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      user?.subscription === 'premium' ? 'bg-amber-100' :
                      user?.subscription === 'standard' ? 'bg-blue-100' : 'bg-muted'
                    }`}>
                      <CreditCard className={`w-5 h-5 ${
                        user?.subscription === 'premium' ? 'text-amber-600' :
                        user?.subscription === 'standard' ? 'text-blue-600' : 'text-muted-foreground'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        {usageDetails?.subscription_name || (user?.subscription === 'premium' ? '⭐ Premium' :
                         user?.subscription === 'standard' ? '🔷 Standard' : t('free'))}
                        {usageDetails?.price_display && usageDetails.price_display !== 'Gratuit' && (
                          <span className="text-xs text-muted-foreground">({usageDetails.price_display})</span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {user?.subscription_expires ? (
                          <>
                            {t('expires_on')}: {new Date(user.subscription_expires).toLocaleDateString()}
                          </>
                        ) : (
                          t('no_expiration')
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Usage alerts */}
                {usageDetails?.alerts?.length > 0 && (
                  <div className="space-y-2">
                    {usageDetails.alerts.map((alert, idx) => (
                      <div key={idx} className={`flex items-start gap-2 p-2 rounded-lg text-sm ${
                        alert.severity === 'critical' ? 'bg-destructive/10 text-destructive' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                      }`}>
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{language === 'fr' ? alert.message_fr : language === 'pt' ? (alert.message_pt || alert.message_en) : alert.message_en}</span>
                      </div>
                    ))}
                    {usageDetails.can_upgrade && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="w-full mt-1"
                        onClick={() => setShowSubscriptionModal(true)}
                      >
                        <Zap className="w-3 h-3 mr-1" />
                        {language === 'fr' ? `Passer à ${usageDetails.next_tier_limits?.name}` : language === 'pt' ? `Atualizar para ${usageDetails.next_tier_limits?.name}` : `Upgrade to ${usageDetails.next_tier_limits?.name}`}
                      </Button>
                    )}
                  </div>
                )}
                
                {/* Subscription features - dynamically loaded from API */}
                <div className="bg-muted/50 rounded-lg p-3 space-y-3 text-sm">
                  <p className="font-medium text-muted-foreground">
                    {t('your_plan_includes')}
                  </p>
                  
                  {loadingUsage ? (
                    <div className="animate-pulse space-y-2">
                      {[1,2,3].map(i => <div key={i} className="h-4 bg-muted rounded w-3/4"></div>)}
                    </div>
                  ) : usageDetails?.limits ? (
                    <div className="space-y-2">
                      {/* Family members */}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{language === 'fr' ? 'Membres famille' : language === 'pt' ? 'Membros família' : 'Family members'}</span>
                        <span className="font-medium">{formatLimit(usageDetails.limits.family_members_max)}</span>
                      </div>
                      {usageDetails.usage?.family_members > 0 && usageDetails.limits.family_members_max > 0 && (
                        <Progress value={usageDetails.usage_percent?.family_members} className="h-1.5" />
                      )}
                      
                      {/* AI Questions */}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{language === 'fr' ? 'Questions IA/mois' : language === 'pt' ? 'Perguntas IA/mês' : 'AI Questions/month'}</span>
                        <span className="font-medium">{formatLimit(usageDetails.limits.ai_questions_per_month)}</span>
                      </div>
                      {usageDetails.limits.ai_questions_per_month > 0 && (
                        <Progress value={usageDetails.usage_percent?.ai_questions} className="h-1.5" />
                      )}
                      
                      {/* OCR Scans */}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{language === 'fr' ? 'Scans OCR/mois' : language === 'pt' ? 'Digitalizações OCR/mês' : 'OCR Scans/month'}</span>
                        <span className="font-medium">{formatLimit(usageDetails.limits.ocr_scans_per_month)}</span>
                      </div>
                      {usageDetails.limits.ocr_scans_per_month > 0 && (
                        <Progress value={usageDetails.usage_percent?.ocr_scans} className="h-1.5" />
                      )}
                      
                      {/* Invoices */}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{language === 'fr' ? 'Factures stockées' : language === 'pt' ? 'Faturas armazenadas' : 'Stored invoices'}</span>
                        <span className="font-medium">
                          {usageDetails.limits.invoice_limit === -1 
                            ? formatLimit(-1) 
                            : `${usageDetails.usage?.invoices || 0}/${usageDetails.limits.invoice_limit}`}
                        </span>
                      </div>
                      {usageDetails.limits.invoice_limit > 0 && (
                        <Progress value={usageDetails.usage_percent?.invoices} className="h-1.5" />
                      )}
                      
                      {/* Recurring subscriptions */}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{language === 'fr' ? 'Abonnements suivis' : language === 'pt' ? 'Assinaturas rastreadas' : 'Tracked subscriptions'}</span>
                        <span className="font-medium">
                          {usageDetails.limits.recurring_subscriptions_limit === -1 
                            ? formatLimit(-1) 
                            : `${usageDetails.usage?.recurring_subscriptions || 0}/${usageDetails.limits.recurring_subscriptions_limit}`}
                        </span>
                      </div>
                      {usageDetails.limits.recurring_subscriptions_limit > 0 && (
                        <Progress value={usageDetails.usage_percent?.recurring_subscriptions} className="h-1.5" />
                      )}
                      
                      {/* Transactions this month */}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{language === 'fr' ? 'Transactions ce mois' : language === 'pt' ? 'Transações este mês' : 'Transactions this month'}</span>
                        <span className="font-medium">
                          {usageDetails.limits.transactions_per_month === -1 
                            ? formatLimit(-1) 
                            : `${usageDetails.usage?.transactions_this_month || 0}/${usageDetails.limits.transactions_per_month}`}
                        </span>
                      </div>
                      {usageDetails.limits.transactions_per_month > 0 && (
                        <Progress value={usageDetails.usage_percent?.transactions} className="h-1.5" />
                      )}
                      
                      {/* Features */}
                      <div className="pt-2 border-t border-border/50 space-y-1">
                        <div className="flex items-center gap-2">
                          {usageDetails.limits.voice_ai ? (
                            <Check className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <X className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                          <span className={usageDetails.limits.voice_ai ? '' : 'text-muted-foreground'}>
                            {language === 'fr' ? 'Assistant vocal' : language === 'pt' ? 'Assistente de voz' : 'Voice assistant'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {usageDetails.limits.predictions ? (
                            <Check className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <X className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                          <span className={usageDetails.limits.predictions ? '' : 'text-muted-foreground'}>
                            {language === 'fr' ? 'Prédictions IA' : language === 'pt' ? 'Previsões IA' : 'AI predictions'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {usageDetails.limits.full_export ? (
                            <Check className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <X className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                          <span className={usageDetails.limits.full_export ? '' : 'text-muted-foreground'}>
                            {language === 'fr' ? 'Export complet' : language === 'pt' ? 'Exportação completa' : 'Full export'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Fallback to hardcoded if API fails
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• {language === 'fr' ? 'Chargement...' : language === 'pt' ? 'A carregar...' : 'Loading...'}</li>
                    </ul>
                  )}
                </div>
                
                {/* Upgrade/Manage buttons */}
                <div className="flex gap-2">
                  {user?.subscription !== 'premium' && (
                    <Button className="flex-1" onClick={() => setShowSubscriptionModal(true)}>
                      <Zap className="w-4 h-4 mr-2" />
                      {t('change_plan')}
                    </Button>
                  )}
                  {user?.subscription !== 'free' && (
                    <Button 
                      variant="outline" 
                      className="text-destructive hover:text-destructive"
                      onClick={() => setShowCancelModal(true)}
                    >
                      {t('cancel')}
                    </Button>
                  )}
                </div>
              </CardContent>
          </Card>
        </motion.div>
        )}

        {/* Situation - only if user is not in a family AND not a child */}
        {!user?.family_id && !isChild && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
          >
            <h3 className="text-sm font-medium text-muted-foreground mb-3 px-1">
              {t('situation')}
            </h3>
            <Card data-testid="situation-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {t('personal_situation')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {getCurrentSituation().emoji} {language === 'fr' ? getCurrentSituation().name_fr : (language === 'pt' ? getCurrentSituation().name_pt : getCurrentSituation().name_en)}
                      </p>
                    </div>
                  </div>
                  <Select value={user?.situation || 'single'} onValueChange={handleSituationChange}>
                    <SelectTrigger className="w-36" data-testid="situation-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SITUATIONS.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.emoji} {language === 'fr' ? s.name_fr : (language === 'pt' ? s.name_pt : s.name_en)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* AI Personality */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3 className="text-sm font-medium text-muted-foreground mb-3 px-1">
            {t('ai_personality')}
          </h3>
          <Card data-testid="personality-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {t('ai_advisor')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {getCurrentPersonality().emoji} {language === 'fr' ? getCurrentPersonality().name_fr : (language === 'pt' ? getCurrentPersonality().name_pt : getCurrentPersonality().name_en)}
                    </p>
                  </div>
                </div>
                <Select value={user?.ai_personality || 'pote'} onValueChange={handlePersonalityChange}>
                  <SelectTrigger className="w-32" data-testid="personality-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AI_PERSONALITIES.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.emoji} {language === 'fr' ? p.name_fr : (language === 'pt' ? p.name_pt : p.name_en)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Referral Program - Hidden for children */}
        {!isChild && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card 
              className="cursor-pointer hover:border-primary/50 transition-colors bg-gradient-to-r from-primary/5 to-accent/5"
              onClick={() => navigate('/referral')}
              data-testid="referral-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{t('referral')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('earn_free_premium')}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Custom Categories */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
        >
          <Card 
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => navigate('/categories')}
            data-testid="categories-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Tag className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {t('categories')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('manage_custom_categories')}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Income Sources (multi-revenues) - Hidden for children */}
        {!isChild && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.185 }}
          >
            <Card
              className="cursor-pointer hover:border-primary/50 transition-colors bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20"
              onClick={() => setShowIncomeSources(true)}
              data-testid="income-sources-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium">{t('income_sources_title')}</p>
                      <p className="text-sm text-muted-foreground">{t('income_sources_desc')}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Income Adjustment - Hidden for children */}
        {!isChild && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.187 }}
          >
            <Card 
              className="cursor-pointer hover:border-primary/50 transition-colors bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20"
              onClick={() => setShowIncomeAdjustment(true)}
              data-testid="income-adjustment-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {t('adjust_income')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('adjust_income_desc')}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Install App PWA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.19 }}
        >
          <Card className="border-dashed">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {t('install_app')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('quick_access')}
                    </p>
                  </div>
                </div>
                <InstallButton />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-sm font-medium text-muted-foreground mb-3 px-1">
            {t('preferences')}
          </h3>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {/* Language */}
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                    <Globe className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <span className="font-medium">{t('language')}</span>
                    <p className="text-xs text-muted-foreground">
                      {t('interface_language')}
                    </p>
                  </div>
                </div>
                <Select value={language} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-40" data-testid="language-select">
                    <SelectValue>
                      {language === 'fr' && '🇫🇷 Français'}
                      {language === 'en' && '🇬🇧 English'}
                      {language === 'pt' && '🇵🇹 Português'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">
                      <span className="flex items-center gap-2">🇫🇷 Français</span>
                    </SelectItem>
                    <SelectItem value="en">
                      <span className="flex items-center gap-2">🇬🇧 English</span>
                    </SelectItem>
                    <SelectItem value="pt">
                      <span className="flex items-center gap-2">🇵🇹 Português</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Theme */}
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                    <Palette className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span className="font-medium">{t('theme')}</span>
                </div>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger className="w-36" data-testid="theme-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {THEME_OPTIONS.map(opt => {
                      const Icon = opt.icon;
                      return (
                        <SelectItem key={opt.id} value={opt.id}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {language === 'fr' ? opt.label_fr : (language === 'pt' ? opt.label_pt : opt.label_en)}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Notifications */}
            </CardContent>
          </Card>
        </motion.div>

        {/* Notifications Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <h3 className="text-sm font-medium text-muted-foreground mb-3 px-1">
            {t('notifications')}
          </h3>
          <NotificationSettings />
        </motion.div>

        {/* Other Options */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="text-sm font-medium text-muted-foreground mb-3 px-1">
            {t('other_label')}
          </h3>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {/* Privacy */}
              <button 
                className="flex items-center justify-between p-4 w-full hover:bg-muted/50 transition-colors"
                onClick={() => navigate('/privacy')}
                data-testid="privacy-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                    <Shield className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span className="font-medium">{t('privacy')}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>

              {/* Help */}
              <button 
                className="flex items-center justify-between p-4 w-full hover:bg-muted/50 transition-colors"
                onClick={() => navigate('/help')}
                data-testid="help-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span className="font-medium">{t('help')}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>

              {/* Contact Support */}
              <button 
                className="flex items-center justify-between p-4 w-full hover:bg-muted/50 transition-colors"
                onClick={() => setShowContactSupport(true)}
                data-testid="contact-support-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <span className="font-medium">{t('contact_support') || 'Contacter le support'}</span>
                    <p className="text-xs text-muted-foreground">
                      {t('contact_support_short') || 'Problème, suggestion, question'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>

              {/* Replay Tutorial */}
              <button 
                className="flex items-center justify-between p-4 w-full hover:bg-muted/50 transition-colors"
                onClick={handleResetTutorial}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                    <RotateCcw className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span className="font-medium">
                    {t('replay_tutorial')}
                  </span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Logout */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button 
            variant="destructive" 
            className="w-full"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('logout')}
          </Button>
        </motion.div>

        {/* App Info */}
        <div className="text-center text-sm text-muted-foreground pt-4 pb-20">
          <p>SpenSpot v1.0.0</p>
          <p>{t('made_with_love')}</p>
        </div>
      </main>

      {/* Income Adjustment Modal */}
      <IncomeAdjustmentModal
        open={showIncomeAdjustment}
        onOpenChange={setShowIncomeAdjustment}
        baseSalary={user?.salary}
      />

      {/* Income Sources Modal (multi-revenues) */}
      <IncomeSourcesModal
        open={showIncomeSources}
        onOpenChange={setShowIncomeSources}
      />

      {/* Subscription Modal */}
      <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('change_plan')}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Choisissez le plan qui correspond à vos besoins'
                : 'Choose the plan that fits your needs'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {plans.length > 0 ? plans.map((plan) => {
              const isCurrentPlan = user?.subscription === plan.type;
              const borderColor = plan.type === 'premium' 
                ? (isCurrentPlan ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-border hover:border-amber-300')
                : plan.type === 'standard'
                  ? (isCurrentPlan ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-border hover:border-blue-300')
                  : (isCurrentPlan ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50');
              const checkColor = plan.type === 'premium' ? 'text-amber-500' 
                : plan.type === 'standard' ? 'text-blue-500' : 'text-primary';
              const emoji = plan.type === 'premium' ? '⭐ ' : plan.type === 'standard' ? '🔷 ' : '';
              
              return (
                <button
                  key={plan.type}
                  onClick={() => handleSubscriptionChange(plan.type)}
                  disabled={isCurrentPlan || updatingSubscription}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${borderColor} disabled:opacity-50`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold">{emoji}{plan.name}</span>
                    {isCurrentPlan && <Check className={`w-5 h-5 ${checkColor}`} />}
                  </div>
                  <p className="text-sm text-muted-foreground">{getPlanDescription(plan)}</p>
                  <p className="text-lg font-bold mt-2">
                    {plan.price_display || `${plan.price}€`}
                    {plan.price > 0 && <span className="text-sm font-normal">/mois</span>}
                  </p>
                </button>
              );
            }) : (
              // Fallback to hardcoded if API fails
              <>
                <button
                  onClick={() => handleSubscriptionChange('free')}
                  disabled={user?.subscription === 'free' || updatingSubscription}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    user?.subscription === 'free' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  } disabled:opacity-50`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold">Gratuit</span>
                    {user?.subscription === 'free' && <Check className="w-5 h-5 text-primary" />}
                  </div>
                  <p className="text-sm text-muted-foreground">2 membres, 5 scans OCR/mois</p>
                  <p className="text-lg font-bold mt-2">0€<span className="text-sm font-normal">/mois</span></p>
                </button>

                <button
                  onClick={() => handleSubscriptionChange('standard')}
                  disabled={user?.subscription === 'standard' || updatingSubscription}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    user?.subscription === 'standard' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-border hover:border-blue-300'
                  } disabled:opacity-50`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold">🔷 Standard</span>
                    {user?.subscription === 'standard' && <Check className="w-5 h-5 text-blue-500" />}
                  </div>
                  <p className="text-sm text-muted-foreground">4 membres, export</p>
                  <p className="text-lg font-bold mt-2">2,99€<span className="text-sm font-normal">/mois</span></p>
                </button>

                <button
                  onClick={() => handleSubscriptionChange('premium')}
                  disabled={user?.subscription === 'premium' || updatingSubscription}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    user?.subscription === 'premium' 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-border hover:border-amber-300'
                  } disabled:opacity-50`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold">⭐ Premium</span>
                    {user?.subscription === 'premium' && <Check className="w-5 h-5 text-amber-500" />}
                  </div>
                  <p className="text-sm text-muted-foreground">Membres illimités, IA illimitée, assistant vocal</p>
                  <p className="text-lg font-bold mt-2">6,99€<span className="text-sm font-normal">/mois</span></p>
                </button>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscriptionModal(false)}>
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {t('cancel_subscription')}
            </DialogTitle>
            <DialogDescription>
              {t('cancel_subscription_warning')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <p className="font-medium text-amber-800 mb-2">
                {t('you_will_lose')}
              </p>
              <ul className="space-y-1 text-amber-700">
                {user?.subscription === 'premium' && (
                  <>
                    <li>• {t('voice_ai_assistant')}</li>
                    <li>• {t('unlimited_ai_questions')}</li>
                  </>
                )}
                {(user?.subscription === 'premium' || user?.subscription === 'standard') && (
                  <>
                    <li>• {t('unlimited_transactions')}</li>
                    <li>• {t('member_limit_reduced')}</li>
                  </>
                )}
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              {t('keep_subscription')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelSubscription}
              disabled={updatingSubscription}
            >
              {updatingSubscription ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  {t('confirm_cancellation')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Support Modal */}
      <ContactSupportModal 
        open={showContactSupport} 
        onOpenChange={setShowContactSupport} 
      />

      {/* Onboarding Tutorial */}
      <Suspense fallback={null}>
        {tutorialActive && (
          <OnboardingTutorial onComplete={() => setTutorialActive(false)} />
        )}
      </Suspense>

      <BottomNav />
    </div>
  );
}
